import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import { useLang } from "@src/hooks/useLang";
import Text from "@components/Text";
import Button from "@components/Button";

/**
 * Details card for a clicked yield/camera cell. Renders the cell's value, its
 * exact WGS84 coordinates, and share actions (open / copy Google Maps link,
 * copy coordinates) so the ground location can be sent to a scout.
 *
 * Content-only: the host provides the chrome — a map-anchored OpenLayers overlay
 * card on desktop, a MobileDrawer on mobile (see Map.tsx). Reads `vm.pinnedCell`
 * directly and renders nothing when no cell is pinned.
 *
 * The Google Maps link is built entirely client-side from coordinates already in
 * memory — no network request.
 */

const gmapsUrl = (lat: number, lng: number) =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

/** Clipboard write with a fallback for non-HTTPS contexts (no navigator.clipboard). */
const copyText = async (text: string): Promise<void> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    /* fall through to the execCommand fallback */
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    document.execCommand("copy");
  } catch {
    /* best-effort; user can still read the coordinates on-screen */
  }
  document.body.removeChild(ta);
};

const Body = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: "Nunito Sans", sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #899093;
  font-size: 20px;
  line-height: 1;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: #494c5b;
  }
`;

const Coords = styled(Text)`
  user-select: text;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  & > * {
    flex: 1;
  }
`;

type TCopied = null | "link" | "coords";

const MapPixelDetails: React.FC = () => {
  const vm = useFieldsScreenVM();
  const lang = useLang();
  const cell = vm.pinnedCell;
  const [copied, setCopied] = useState<TCopied>(null);

  // Escape closes the pinned card.
  useEffect(() => {
    if (!cell) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") vm.clearPinnedCell();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cell, vm]);

  // Reset the transient "Copied" state whenever a different cell is pinned.
  useEffect(() => {
    setCopied(null);
  }, [cell]);

  if (!cell) return null;

  const t = (key: string, fallback: string): string =>
    (lang.fields as Record<string, string | undefined>)?.[key] ?? fallback;

  const url = gmapsUrl(cell.lat, cell.lng);
  const coordsText = `${cell.lat.toFixed(6)}, ${cell.lng.toFixed(6)}`;

  const flash = (which: Exclude<TCopied, null>) => {
    setCopied(which);
    window.setTimeout(() => setCopied((c) => (c === which ? null : c)), 1500);
  };

  const copiedLabel = `${t("cellCopied", "Copied")} ✓`;

  return (
    <Body>
      <Header>
        <Text type="heading" size={12} color="#899093" fitContent nowrap>
          {t("cellLocationTitle", "Cell location")}
        </Text>
        <CloseButton onClick={() => vm.clearPinnedCell()} aria-label="Close">
          ×
        </CloseButton>
      </Header>

      <Text type="heading" size={16} color="#494C5B">
        {cell.label}
      </Text>
      <Coords type="text" size={12} color="#899093">
        {coordsText}
      </Coords>

      <Button
        kind="primary"
        onClick={() => window.open(url, "_blank", "noopener")}
      >
        {t("cellOpenInGoogleMaps", "Open in Google Maps")}
      </Button>
      <ButtonRow>
        <Button kind="outline" onClick={() => copyText(url).then(() => flash("link"))}>
          {copied === "link" ? copiedLabel : t("cellCopyLink", "Copy link")}
        </Button>
        <Button
          kind="outline"
          onClick={() => copyText(coordsText).then(() => flash("coords"))}
        >
          {copied === "coords" ? copiedLabel : t("cellCopyCoordinates", "Copy coordinates")}
        </Button>
      </ButtonRow>
    </Body>
  );
};

export default observer(MapPixelDetails);
